import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SchoolAvatar from './SchoolAvatar';
import { getApiBaseUrl } from '../apiBase';

const STORAGE_KEY = 'memberSchoolId';

export function getSelectedMemberSchoolId() {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? parseInt(v, 10) : null;
}

export function setSelectedMemberSchoolId(schoolId) {
    if (schoolId != null) localStorage.setItem(STORAGE_KEY, String(schoolId));
}

export default function MemberSchoolSwitcher({ compact = false, onSchoolChange }) {
    const { t } = useTranslation(['schoolSwitcher', 'balance']);
    const [profiles, setProfiles] = useState([]);
    const [schoolId, setSchoolId] = useState(getSelectedMemberSchoolId());
    const [open, setOpen] = useState(false);
    const role = localStorage.getItem('role');
    const wrapRef = useRef(null);

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        if (role !== 'Member' || !token) return;
        loadProfiles();
    }, [role, token]);

    useEffect(() => {
        if (!open) return;
        function onDoc(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    async function loadProfiles() {
        try {
            const res = await api.get('/student/me');
            const list = res.data || [];
            setProfiles(list);
            if (list.length === 0) return;

            const stored = getSelectedMemberSchoolId();
            const valid = list.find(p => p.schoolId === stored);
            const sid = valid ? stored : list[0].schoolId;
            setSchoolId(sid);
            setSelectedMemberSchoolId(sid);
            onSchoolChange?.(list.find(p => p.schoolId === sid) || list[0]);
        } catch {
            /* ignore */
        }
    }

    function selectSchool(profile) {
        if (!profile) return;
        setSchoolId(profile.schoolId);
        setSelectedMemberSchoolId(profile.schoolId);
        setOpen(false);
        onSchoolChange?.(profile);
        window.dispatchEvent(new CustomEvent('member-school-changed', {
            detail: { schoolId: profile.schoolId, profile }
        }));
    }

    if (role !== 'Member' || profiles.length === 0) return null;
    if (profiles.length === 1 && !compact) return null;

    const current = profiles.find(p => p.schoolId === schoolId) || profiles[0];
    const avatarSize = compact ? 32 : 40;

    return (
        <div
            ref={wrapRef}
            style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                flexWrap: 'wrap',
                position: 'relative',
                ...(compact ? {} : { marginBottom: '16px' })
            }}
        >
            {!compact && (
                <span style={{ fontSize: '13px', color: '#555', fontWeight: '600' }}>{t('schoolSwitcher:schoolLabel')}</span>
            )}

            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: compact ? '4px 10px 4px 4px' : '6px 14px 6px 6px',
                    borderRadius: '999px',
                    border: compact ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ddd',
                    background: compact ? 'rgba(255,255,255,0.1)' : 'white',
                    color: compact ? 'white' : '#1A1A1A',
                    cursor: 'pointer',
                    fontSize: compact ? '12px' : '14px',
                    fontWeight: '600',
                    maxWidth: compact ? '220px' : '360px'
                }}
            >
                <SchoolAvatar logoPath={current?.schoolLogo} size={avatarSize} alt={current?.schoolName} />
                <span style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                    {current?.schoolName}
                </span>
                <span style={{ opacity: 0.6, fontSize: '10px' }}>▼</span>
            </button>

            {open && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: compact ? 'auto' : 0,
                    right: compact ? 0 : 'auto',
                    marginTop: '6px',
                    minWidth: '240px',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    border: '1px solid var(--sp-gray-100)',
                    zIndex: 1100,
                    overflow: 'hidden'
                }}>
                    {profiles.map(p => (
                        <button
                            key={p.schoolId}
                            type="button"
                            onClick={() => selectSchool(p)}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 14px',
                                border: 'none',
                                borderBottom: '1px solid #f0f0f0',
                                background: p.schoolId === schoolId ? '#f3eeff' : 'white',
                                cursor: 'pointer',
                                textAlign: 'left'
                            }}
                        >
                            <SchoolAvatar logoPath={p.schoolLogo} size={36} alt={p.schoolName} />
                            <span style={{ fontWeight: '600', color: '#1A1A1A', fontSize: '14px' }}>
                                {p.schoolName}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {!compact && current?.walletScope?.isSharedWallet && (
                <span style={{
                    fontSize: '12px', color: '#EB3333', background: '#f3eeff',
                    padding: '4px 10px', borderRadius: '8px'
                }}>
                    {t('balance:sharedWallet', { names: current.walletScope.schoolNames?.join(', ') })}
                </span>
            )}
        </div>
    );
}
